import React, { useState, useEffect } from 'react';
import { Users, ArrowLeft, ArrowRight, User } from 'lucide-react';
import { fetchSellerPerformance } from '../../../services/salesInvoiceService';
import { fmtNum } from '../../../helpers/Executive-Dashboard/formatters';
import { integration } from '../../../helpers/Executive-Dashboard/integration';

export const SellerPerformanceCard = React.memo(({ sourceLabel, branch, onViewModeChange }) => {
    const [viewMode, setViewMode] = useState('permonth');
    const [slideDirection, setSlideDirection] = useState('none');
    const [slideKey, setSlideKey] = useState(0);
    const now = new Date();
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(now.getFullYear());
    const [sellersData, setSellersData] = useState([]);
    const [dateRangeStr, setDateRangeStr] = useState('');
    const [sellerLoading, setSellerLoading] = useState(true);
    const [sellerError, setSellerError] = useState(null);

    const loadSellerData = async (m, y) => {
        setSellerLoading(true);
        setSellerError(null);
        try {
            const config = sourceLabel === 'Gelora Printing' ? integration('GP') : integration('SUMA');
            const result = await fetchSellerPerformance(viewMode, m, y, config, branch);
            setSellersData(result.sellers || []);
            setDateRangeStr(result.dateRange || '');
        } catch (err) {
            setSellerError(err.message);
        } finally {
            setSellerLoading(false);
        }
    };

    useEffect(() => {
        loadSellerData(month, year);
    }, [month, year, sourceLabel, viewMode, branch]);

    const handlePrev = () => {
        setSlideDirection('right');
        setSlideKey(k => k + 1);
        if (month === 1) { setMonth(12); setYear(y => y - 1); }
        else setMonth(m => m - 1);
    };

    const handleNext = () => {
        setSlideDirection('left');
        setSlideKey(k => k + 1);
        if (month === 12) { setMonth(1); setYear(y => y + 1); }
        else setMonth(m => m + 1);
    };

    // Compute max sales for percentage calculation
    const maxSales = sellersData.length > 0 ? Math.max(...sellersData.map(s => s.totalSales)) : 1;

    const animName = slideDirection === 'left' ? 'sp-slide-left' : slideDirection === 'right' ? 'sp-slide-right' : 'sp-fade-in';

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Users size={16} style={{ color: '#0ea5e9' }} />
                    {sourceLabel === 'Gelora Printing' ? 'Performa Perwilayah' : 'Performa Penjual' }
                </h3>
                <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '8px', padding: '4px' }}>
                    <button
                        onClick={() => { setViewMode('all'); onViewModeChange?.('all'); }}
                        style={{
                            padding: '6px 14px', borderRadius: '6px', border: 'none',
                            fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                            background: viewMode === 'all' ? '#fff' : 'transparent',
                            color: viewMode === 'all' ? '#0ea5e9' : '#64748b',
                            boxShadow: viewMode === 'all' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s'
                        }}>
                        All
                    </button>
                    <button
                        onClick={() => { setViewMode('permonth'); onViewModeChange?.('permonth'); }}
                        style={{
                            padding: '6px 14px', borderRadius: '6px', border: 'none',
                            fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                            background: viewMode === 'permonth' ? '#fff' : 'transparent',
                            color: viewMode === 'permonth' ? '#0ea5e9' : '#64748b',
                            boxShadow: viewMode === 'permonth' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s'
                        }}>
                        Per Month
                    </button>
                </div>
            </div>
            
            {viewMode === 'permonth' && (
            <div style={{ padding: '10px 20px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <div style={{ color: '#0ea5e9', fontWeight: '600', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div 
                        onClick={handlePrev}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: '24px', height: '24px', borderRadius: '6px', 
                            backgroundColor: '#fff', cursor: 'pointer',
                            fontSize: '14px', fontWeight: 'bold', color: '#64748b',
                            transition: 'all 0.2s', border: '1px solid #e2e8f0'
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.color = '#334155'; }}
                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#64748b'; }}
                    >
                        <ArrowLeft size={14} />
                    </div>
                    
                    <span key={`label-${slideKey}`} style={{ fontSize: '13px', color: '#0ea5e9', minWidth: '130px', textAlign: 'center', animation: `${animName} 0.35s ease-out` }}>
                        {sellerLoading ? '...' : dateRangeStr}
                    </span>
                    <div 
                        onClick={handleNext}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: '24px', height: '24px', borderRadius: '6px', 
                            backgroundColor: '#fff', cursor: 'pointer',
                            fontSize: '14px', fontWeight: 'bold', color: '#64748b',
                            transition: 'all 0.2s', border: '1px solid #e2e8f0'
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.color = '#334155'; }}
                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#64748b'; }}
                    >
                        <ArrowRight size={14} />
                    </div>
                </div>
            </div>
            )}
            
            <div style={{ overflowX: 'auto', overflowY: 'auto', flex: 1, animation: `${animName} 0.4s ease-out` }} key={`content-${slideKey}`}>
                {sellerLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '13px' }}>Loading...</div>
                ) : sellerError ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '8px' }}>{sellerError}</div>
                        <button onClick={() => loadSellerData(month, year)} style={{ padding: '6px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Retry</button>
                    </div>
                ) : sellersData.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '13px' }}>Tidak ada data penjual</div>
                ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <th style={{ padding: '12px 20px', textAlign: 'left', fontWeight: '600', color: '#0f172a' }}>Penjual</th>
                            <th style={{ padding: '12px 0px', textAlign: 'right', fontWeight: '600', color: '#0f172a' }}>Total Penjualan</th>
                            <th style={{ padding: '12px 20px', textAlign: 'right', fontWeight: '600', color: '#0f172a', width: '80px' }}>Invoice</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sellersData.map((item, i) => {
                            const pct = maxSales > 0 ? Math.round((item.totalSales / maxSales) * 100) : 0;
                            return (
                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: '#fff' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}>
                                <td style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ width: '30px', height: '30px', borderRadius: '50%', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                                        <User size={16} />
                                    </div>
                                    <span style={{ color: '#0f172a', fontWeight: '500', fontSize: '13px', width: '5rem' }}>{item.name}</span>
                                </td>
                                <td style={{ padding: '14px 0px', textAlign: 'right' }}>
                                    <span style={{ color: '#94a3b8', fontSize: '10px', marginRight: '4px' }}>Rp</span>
                                    <span style={{ color: '#0f172a', fontWeight: '600', fontSize: '13px' }}>{item.totalSales > 0 ? fmtNum(item.totalSales) : '-'}</span>
                                    {/* percentage perfomance */}
                                    {/* <span style={{ 
                                        display: 'inline-block', padding: '2px 8px', borderRadius: '12px', 
                                        backgroundColor: '#e0f2fe', color: '#0ea5e9', fontSize: '11px', fontWeight: '600', 
                                        border: '1px solid #bae6fd', marginLeft: '8px', minWidth: '40px', textAlign: 'center'
                                    }}>
                                        {0}%
                                    </span> */}
                                </td>
                                <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                                    <span style={{ color: '#0f172a', fontWeight: '600' }}>{fmtNum(item.invoiceCount)}</span>
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
                )}
            </div>
        </div>
    );
});